import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {BLOCKS_CONFIG} from '@blocks/blocks.config';
import * as uuid from 'uuid';
import {ControlContainer, FormControl, NgForm, ValidationErrors} from '@angular/forms';
import {SessionControllerService} from 'projects/submission/src/app/session/session-controller.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {TranslocoService} from '@ngneat/transloco';
import {ContentLocale, ToastService} from '@paperlessio/sdk/api/util';
import {BlockComponent} from '@blocks/block/block.component';
import {LocalizationService} from '@shared/i18n/localization.service';
import {DataSetInput} from '@paperlessio/sdk/api/models';

@Component({
  selector: 'pl-data-set-input',
  templateUrl: './data-set-input.component.html',
  styleUrls: ['./data-set-input.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class DataSetInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() block: DataSetInput;

  @Input() data: string[];
  @Output() dataChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  @HostBinding('attr.data-slug')
  @Input() slug: string;

  public blocksConfig = inject(BLOCKS_CONFIG, {});

  private form = inject(NgForm, {});
  private sessionController = inject(SessionControllerService, {optional: true});
  private cdr = inject(ChangeDetectorRef, {});

  public domId = '';
  public formControl: FormControl<string[]>;
  public ineligible = false;
  public rowAdded = false;
  public translate = inject(TranslocoService);
  private blockComponent = inject(BlockComponent);
  private toast = inject(ToastService);
  private localizationService = inject(LocalizationService);

  protected readonly Infinity = Infinity;

  get rows(): string[] {
    return this.data;
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  ngOnInit(): void {
    if (this.blocksConfig.submittable) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    this.formControl = new FormControl<string[]>(this.data, () => this._valid());
    this.form.form.addControl(this.slug, this.formControl);

    // if minRows is set and there are no rows, add the minimum number of rows in submission mode
    if (this.blocksConfig.submittable && !this.blocksConfig.editable && this.block.settings.minRows > 0 && this.data.length === 0) {
      for (let i = 0; i < this.block.settings.minRows; i++) {
        this.data.push(uuid.v4());
      }

      this.setData(this.data);
    }

    // in printOptimized mode, we add exactly one row if there are no rows
    if (this.blocksConfig.printOptimized && this.data.length === 0) {
      this.data.push(uuid.v4());
      this.setData(this.data);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if slug has changed, we need to update the form control
    if (changes.slug && this.formControl) {
      this.form.form.removeControl(changes.slug.previousValue);
      this.form.form.addControl(changes.slug.currentValue, this.formControl);
    }
  }

  ngOnDestroy(): void {
    this.form.form.removeControl(this.slug);
  }

  addNewRow() {
    if (this.blocksConfig.submittable && !this.ineligible) {
      this.rowAdded = true;
      this.setData([...(this.data || []), uuid.v4()]);
    }
  }

  deleteRow(row: string) {
    if (this.blocksConfig.submittable && !this.ineligible) {
      this.setData(this.data.filter(r => r !== row));
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    const newData = [...this.data];
    moveItemInArray(newData, event.previousIndex, event.currentIndex);
    this.setData(newData);
  }

  get validationInfo(): string {
    const translationBase = 'blocks.Block::Input::DataSetInput.validationInfo';

    if (this.block.settings.maxRows >= 1 && this.data.length === this.block.settings.maxRows) {
      return this.translate.translate(translationBase + '.maxRowCountReached');
    }
  }

  get validationError(): string {
    const translationBase = 'blocks.Block::Input::DataSetInput.validationError';
    const minRows = this.block.settings.minRows || 0;
    const maxRows = this.block.settings.maxRows || Infinity;
    const numberOfMissingRows = minRows - this.data.length;
    const numberOfTooManyRows = this.data.length - maxRows;
    const numberOfRowsStillAllowed = maxRows - this.data.length;

    if(numberOfMissingRows > 0) {
      if(numberOfRowsStillAllowed === 1) {
        return this.translate.translate(`${translationBase}.rowsMissing.oneMoreRowAllowed`);
      } else if(numberOfRowsStillAllowed > 1) {
        if(numberOfMissingRows === 1) {
          return this.translate.translate(`${translationBase}.rowsMissing.severalMoreRowsAllowed.oneRowMissing`);
        } else if(numberOfMissingRows > 1) {
          return this.translate.translate(`${translationBase}.rowsMissing.severalMoreRowsAllowed.severalRowsMissing`, {count: numberOfMissingRows});
        }
      }
    } else if (numberOfTooManyRows > 0) {
      if(numberOfTooManyRows === 1) {
        if(maxRows === 1) {
          return this.translate.translate(`${translationBase}.tooManyRows.oneRowTooMuch.oneRowAllowed`);
        } else if(maxRows > 1) {
          return this.translate.translate(`${translationBase}.tooManyRows.oneRowTooMuch.severalRowsAllowed`, {count: maxRows});
        }
      } else if (numberOfTooManyRows > 1) {
        if(maxRows === 1) {
          return this.translate.translate(`${translationBase}.tooManyRows.severalRowsTooMuch.oneRowAllowed`, {tooManyRows: numberOfTooManyRows});
        } else if(maxRows > 1) {
          return this.translate.translate(`${translationBase}.tooManyRows.severalRowsTooMuch.severalRowsAllowed`, {count: maxRows, tooManyRows: numberOfTooManyRows});
        }
      }
    }
  }

  public onClickInEditor(): void {
    if(this.blockComponent?.selected) {
      this.toast.info('blocks.' + this.block.type + '.add_deactivated');
    }
  }

  private setData(data: string[]) {
    this.data = data;
    this.dataChange.emit(this.data);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.setValue(this.data, {emitViewToModelChange: false});
    this.cdr.markForCheck();
  }

  private _valid(): ValidationErrors | null {
    return this.minRowsSatisfied() && this.maxRowsSatisfied() ? null : {required: true};
  }

  private minRowsSatisfied(): boolean {
    return !this.block.settings.minRows || this.block.settings.minRows === 0 || this.data?.length >= this.block.settings.minRows;
  }

  private maxRowsSatisfied(): boolean {
    return !this.block.settings.maxRows || this.block.settings.maxRows === 0 || this.data?.length <= this.block.settings.maxRows;
  }
}
